import { animate, state, style, transition, trigger } from '@angular/animations';
import { CdkAccordion, CdkAccordionItem } from '@angular/cdk/accordion';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDivider } from '@angular/material/divider';
import {
  MatError,
  MatFormField,
  MatHint,
  MatPrefix,
  MatSuffix,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, EMPTY, tap } from 'rxjs';
import { DialogService } from '../../../components/dialog/dialog.service';
import {
  DependencyNotice,
  PERMISSION_GROUP_INFO,
  PermissionGroupDependencyInfo,
  unmetDependencies,
  WORKS_WITHOUT_GROUP_READ,
} from '../../../models/permission-dependencies.data';
import { ACTION_LABELS, PERMISSION_GROUPS } from '../../../models/permission.model';
import { UserRoleService } from '../../../services/user-role.service';

interface PermissionGroupView {
  key: string;
  label: string;
  permissions: { key: string; value: string; actionLabel: string }[];
}

@Component({
  selector: 'app-role-detail',
  imports: [
    ReactiveFormsModule,
    MatButton,
    MatIconButton,
    MatCheckbox,
    MatFormField,
    MatInput,
    MatIcon,
    MatError,
    MatHint,
    MatSuffix,
    MatPrefix,
    MatProgressSpinner,
    CdkAccordion,
    CdkAccordionItem,
    MatDivider,
    MatTooltip,
  ],
  templateUrl: './role-detail.component.html',
  styleUrl: './role-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({ height: '0', opacity: '0', overflow: 'hidden' })),
      state('expanded', style({ height: '*', opacity: '1', overflow: 'hidden' })),
      transition('collapsed <=> expanded', [
        animate('150ms cubic-bezier(0.4, 0, 0.2, 1)'),
      ]),
    ]),
  ],
})
export class RoleDetailComponent implements OnInit {
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _roleService = inject(UserRoleService);
  private readonly _dialogService = inject(DialogService);

  /** Whether we're in create or edit mode */
  protected readonly mode = signal<'create' | 'edit'>('create');

  /** Loading state for edit mode */
  protected readonly loading = signal(false);

  /** Saving state */
  protected readonly saving = signal(false);

  /** The role ID when editing */
  private _roleId: string | null = null;

  /** Search keyword for filtering permission groups */
  protected readonly permissionSearch = signal('');

  /** Track which groups are expanded — all expanded by default */
  protected readonly expandedGroups = signal(new Set<string>());

  /** Set of currently selected permission strings */
  protected readonly selectedPermissions = signal(new Set<string>());

  protected readonly labelControl = new FormControl('', {
    nonNullable: true,
    validators: [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(64),
    ],
  });

  protected readonly descriptionControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.maxLength(255)],
  });

  protected readonly form = new FormGroup({
    label: this.labelControl,
    description: this.descriptionControl,
  });

  /** All permission groups */
  private readonly _allPermissionGroups: PermissionGroupView[] = Object.entries(
    PERMISSION_GROUPS
  ).map(([key, group]) => ({
    key,
    label: group.label,
    permissions: Object.entries(group.permissions).map(([permKey, permValue]) => {
      const action = permValue.split(':')[1];
      return {
        key: permKey,
        value: permValue,
        actionLabel: ACTION_LABELS[action] ?? action,
      };
    }),
  }));

  /** Filtered permission groups based on search */
  protected readonly permissionGroups = computed(() => {
    const search = this.permissionSearch().toLowerCase().trim();
    if (!search) {
      return this._allPermissionGroups;
    }
    return this._allPermissionGroups.filter(
      (group) =>
        group.label.toLowerCase().includes(search) ||
        group.key.toLowerCase().includes(search) ||
        group.permissions.some(
          (p) =>
            p.actionLabel.toLowerCase().includes(search) ||
            p.value.toLowerCase().includes(search)
        )
    );
  });

  /** Page title */
  protected readonly pageTitle = computed(() =>
    this.mode() === 'create' ? 'Nieuwe rol' : 'Rol bewerken'
  );

  /** Whether all permissions across all groups are selected */
  protected readonly allSelected = computed(() => {
    const selected = this.selectedPermissions();
    return this._allPermissionGroups.every((group) =>
      group.permissions.every((p) => selected.has(p.value))
    );
  });

  /** Count of selected permissions */
  protected readonly selectedPermissionCount = computed(
    () => this.selectedPermissions().size
  );

  /** Total number of available permissions */
  protected readonly totalPermissionCount = this._allPermissionGroups.reduce(
    (sum, group) => sum + group.permissions.length,
    0
  );

  /** Whether some but not all permissions are selected */
  protected readonly someSelected = computed(() => {
    const selected = this.selectedPermissions();
    const hasAny = this._allPermissionGroups.some((group) =>
      group.permissions.some((p) => selected.has(p.value))
    );
    return hasAny && !this.allSelected();
  });

  /** What the selected permissions need from other groups and do not have */
  protected readonly dependencyNotices = computed(() =>
    unmetDependencies(this.selectedPermissions())
  );

  ngOnInit(): void {
    const roleId = this._route.snapshot.paramMap.get('roleId');

    // Initialize all groups as expanded
    const allKeys = new Set(this._allPermissionGroups.map((g) => g.key));
    this.expandedGroups.set(allKeys);

    if (roleId) {
      this.mode.set('edit');
      this._roleId = roleId;
      this._loadRole(roleId);
    }
  }

  protected navigateBack(): void {
    this._router.navigate(['../'], { relativeTo: this._route });
  }

  protected handleSearch(value: string): void {
    this.permissionSearch.set(value);
  }

  protected clearSearch(): void {
    this.permissionSearch.set('');
  }

  // --- Expand / Collapse ---

  protected isGroupExpanded(groupKey: string): boolean {
    return this.expandedGroups().has(groupKey);
  }

  protected toggleGroupExpanded(groupKey: string): void {
    this.expandedGroups.update((set) => {
      const next = new Set(set);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }

  protected expandAll(): void {
    const allKeys = new Set(this._allPermissionGroups.map((g) => g.key));
    this.expandedGroups.set(allKeys);
  }

  protected collapseAll(): void {
    this.expandedGroups.set(new Set());
  }

  // --- Select / Clear all ---

  protected selectAll(): void {
    this.selectedPermissions.update((set) => {
      const next = new Set(set);
      for (const group of this._allPermissionGroups) {
        for (const p of group.permissions) {
          next.add(p.value);
        }
      }
      return next;
    });
  }

  protected clearAll(): void {
    this.selectedPermissions.set(new Set());
  }

  // --- Per-permission toggles ---

  protected isPermissionSelected(permission: string): boolean {
    return this.selectedPermissions().has(permission);
  }

  protected togglePermission(permission: string): void {
    this.selectedPermissions.update((set) => {
      const next = new Set(set);
      if (next.has(permission)) {
        next.delete(permission);
      } else {
        next.add(permission);
      }
      return next;
    });
  }

  // --- Per-group toggles ---

  protected isGroupAllSelected(group: PermissionGroupView): boolean {
    const selected = this.selectedPermissions();
    return group.permissions.every((p) => selected.has(p.value));
  }

  protected isGroupIndeterminate(group: PermissionGroupView): boolean {
    const selected = this.selectedPermissions();
    const count = group.permissions.filter((p) => selected.has(p.value)).length;
    return count > 0 && count < group.permissions.length;
  }

  protected toggleGroup(group: PermissionGroupView): void {
    this.selectedPermissions.update((set) => {
      const next = new Set(set);
      if (this.isGroupAllSelected(group)) {
        for (const p of group.permissions) {
          next.delete(p.value);
        }
      } else {
        for (const p of group.permissions) {
          next.add(p.value);
        }
      }
      return next;
    });
  }

  // --- Dependency info helpers ---

  /** Get group-level dependency info for the ℹ icon */
  protected getGroupInfo(groupKey: string): PermissionGroupDependencyInfo | null {
    return PERMISSION_GROUP_INFO[groupKey] ?? null;
  }

  /** The unmet dependencies of this group's selected permissions, required first */
  protected getDependencyNotices(group: PermissionGroupView): DependencyNotice[] {
    return this.dependencyNotices().filter((notice) =>
      group.permissions.some((p) => p.value === notice.permission)
    );
  }

  /** Grant `permissions` as well: a notice's "Toevoegen" */
  protected addPermissions(permissions: string[]): void {
    this.selectedPermissions.update((set) => new Set([...set, ...permissions]));
  }

  /**
   * Check if a group has non-read permissions selected but is missing
   * the `:read` permission. Returns the warning and that permission, or null.
   *
   * e.g. if `client:create` is selected but `client:read` is not,
   * shows: "Zonder "Cliënten lezen" is de cliënten-pagina niet toegankelijk. …"
   */
  protected getMissingReadHint(
    group: PermissionGroupView
  ): { message: string; permission: string } | null {
    const selected = this.selectedPermissions();

    // Find the :read permission in this group
    const readPerm = group.permissions.find((p) => p.value.endsWith(':read'));
    if (!readPerm) return null;

    // If :read is already selected, no warning needed
    if (selected.has(readPerm.value)) return null;

    // Check if any non-read permission in this group is selected that needs it
    const hasNonRead = group.permissions.some(
      (p) =>
        p.value !== readPerm.value &&
        selected.has(p.value) &&
        !WORKS_WITHOUT_GROUP_READ.has(p.value)
    );

    if (!hasNonRead) return null;

    return {
      message:
        `Zonder "${group.label} lezen" is de ${group.label.toLowerCase()}-pagina ` +
        `niet toegankelijk. De geselecteerde rechten zijn dan niet bruikbaar.`,
      permission: readPerm.value,
    };
  }

  // --- Save ---

  protected handleSave(): void {
    if (this.form.invalid || this.saving()) {
      return;
    }

    this.saving.set(true);

    const payload = {
      label: this.labelControl.value,
      description: this.descriptionControl.value,
      permissions: [...this.selectedPermissions()],
    };

    const save$ =
      this.mode() === 'create'
        ? this._roleService.create(payload)
        : this._roleService.update(this._roleId!, payload);

    save$
      .pipe(
        tap(() => {
          this.saving.set(false);
          this.navigateBack();
        }),
        catchError((error: HttpErrorResponse) => {
          this.saving.set(false);
          const action = this.mode() === 'create' ? 'aanmaken' : 'bewerken';

          const isConflict =
            error.status === 409 &&
            error.error?.detail?.errorCode === 'USER_ROLE_LABEL_EXISTS';

          const message = isConflict
            ? `Er bestaat al een rol met de naam "${this.labelControl.value}". Kies een andere naam.`
            : `Er is iets misgegaan bij het ${action} van de rol. Probeer het later nog eens.`;

          this._dialogService.alert({
            title: 'Foutmelding',
            message,
            confirmText: 'OK',
          });
          return EMPTY;
        })
      )
      .subscribe();
  }

  // --- Private ---

  private _loadRole(roleId: string): void {
    this.loading.set(true);
    this._roleService
      .getById(roleId)
      .pipe(
        tap((role) => {
          this.labelControl.setValue(role.label);
          this.descriptionControl.setValue(role.description ?? '');
          this.selectedPermissions.set(new Set(role.permissions));
          this.loading.set(false);
        }),
        catchError(() => {
          this.loading.set(false);
          this._dialogService.alert({
            title: 'Foutmelding',
            message: 'Kon de rol niet laden. Probeer het later nog eens.',
            confirmText: 'OK',
          });
          this.navigateBack();
          return EMPTY;
        })
      )
      .subscribe();
  }
}
